import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { ArrowSquareOut, Question, CheckCircle, Sparkle, Warning } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { AmazonDisclosure } from './AmazonDisclosure';

/**
 * "Ask Moe" drawer — structured, hand-written expert guidance for a
 * Smart Plan action (v1: fertilize). Side-sheet on desktop, bottom sheet
 * on mobile (shadcn <Sheet> handles that).
 *
 * Props:
 *   open: boolean
 *   onOpenChange: fn(boolean)
 *   actionKey: string (e.g. "fertilization" or "fertilize")
 *   lawnSqft: number — used to auto-calc product amounts
 */
export const AskMoeDrawer = ({ open, onOpenChange, actionKey, lawnSqft = 0 }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !actionKey) return;
    let cancel = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/action-guides/${encodeURIComponent(actionKey)}`, {
        params: { lawn_sqft: lawnSqft || 0 },
      })
      .then((res) => {
        if (!cancel) setData(res.data);
      })
      .catch((e) => {
        if (!cancel) setError(e.response?.data?.detail || 'Guide not available yet.');
      })
      .finally(() => {
        if (!cancel) setLoading(false);
      });

    // Fire "open" event (best effort, silent failure, respects opt-out server-side)
    axios
      .post(
        `${API_URL}/api/analytics/guide-event`,
        { action_guide_key: actionKey, event_type: 'open' },
        { withCredentials: true }
      )
      .catch(() => {});

    return () => {
      cancel = true;
    };
  }, [open, actionKey, lawnSqft]);

  const handleProductClick = (productId) => {
    axios
      .post(
        `${API_URL}/api/analytics/guide-event`,
        { action_guide_key: actionKey, event_type: 'product_click', product_id: productId },
        { withCredentials: true }
      )
      .catch(() => {});
  };

  const rateCalc = data?.rate_calc;
  const sortedProducts = useMemo(() => {
    if (!data?.products) return [];
    // Put lowest-burn-risk (organic) first, then highest slow-release %
    return [...data.products].sort((a, b) => {
      if (a.type === 'organic' && b.type !== 'organic') return -1;
      if (b.type === 'organic' && a.type !== 'organic') return 1;
      return (b.slow_release_pct || 0) - (a.slow_release_pct || 0);
    });
  }, [data]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="ask-moe-drawer"
      >
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2 text-[11px] font-semibold tracking-wider uppercase text-primary">
            <Sparkle weight="fill" size={14} />
            Ask Moe
          </div>
          <SheetTitle className="text-2xl leading-tight" data-testid="ask-moe-title">
            {loading ? 'Loading…' : data?.guide?.headline || 'Guide unavailable'}
          </SheetTitle>
          {data?.guide?.summary && (
            <SheetDescription className="text-sm leading-relaxed text-muted-foreground">
              {data.guide.summary}
            </SheetDescription>
          )}
        </SheetHeader>

        {error && (
          <div className="mt-6 text-sm text-muted-foreground" data-testid="ask-moe-error">
            {error} Moe is still learning this one — check back soon.
          </div>
        )}

        {!error && !loading && data && (
          <div className="mt-6 space-y-6">
            {/* Rate calc summary (if we know sqft) */}
            {rateCalc && lawnSqft > 0 && (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="ask-moe-rate-calc">
                <div className="text-xs font-semibold tracking-wider uppercase text-primary mb-1">
                  Your lawn ({rateCalc.lawn_sqft.toLocaleString()} sq ft)
                </div>
                <div className="text-base font-semibold">
                  With a standard 24-0-4 bag, you'll need ~{rateCalc.total_lbs_needed} lbs of product this round.
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  ({rateCalc.lbs_per_1000_sqft} lbs per 1,000 sq ft)
                </div>
              </div>
            )}

            {/* Expert sections */}
            {data.guide.sections.map((section) => (
              <Section key={section.id} section={section} />
            ))}

            {/* Curated product picks */}
            {sortedProducts.length > 0 && (
              <div data-testid="ask-moe-products">
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle weight="fill" size={16} className="text-primary" />
                  Moe's picks
                </h3>
                {/* FTC-required disclosure positioned ABOVE the affiliate links */}
                <div className="mb-3 rounded-md bg-muted/40 border border-border/60 px-3 py-2">
                  <AmazonDisclosure variant="inline" />
                </div>
                <div className="space-y-3">
                  {sortedProducts.map((p) => (
                    <ProductCard key={p.id} product={p} showSizing={lawnSqft > 0} onClick={() => handleProductClick(p.id)} />
                  ))}
                </div>
              </div>
            )}

            {data.guide.cta_footnote && (
              <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground leading-relaxed">
                {data.guide.cta_footnote}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ── Subcomponents ──
const Section = ({ section }) => {
  // Render simple markdown-ish: **bold**, newlines, bullets starting with • or -
  const renderBody = (body) => {
    const lines = body.split('\n');
    return lines.map((line, idx) => {
      if (!line.trim()) return <div key={idx} className="h-1.5" />;
      const isBullet = /^\s*[•\-\d]+\.?\s/.test(line);
      return (
        <p key={idx} className={`text-sm leading-relaxed text-foreground ${isBullet ? 'pl-2' : ''}`}>
          {renderInline(line)}
        </p>
      );
    });
  };

  const renderInline = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return <strong key={i} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>;
      }
      if (p.startsWith('`') && p.endsWith('`')) {
        return <code key={i} className="px-1 py-0.5 rounded bg-muted text-[12px]">{p.slice(1, -1)}</code>;
      }
      return <span key={i}>{p}</span>;
    });
  };

  return (
    <div data-testid={`ask-moe-section-${section.id}`}>
      <h3 className="text-base font-semibold text-foreground mb-2">{section.title}</h3>
      <div className="space-y-1.5">{renderBody(section.body)}</div>
    </div>
  );
};

const ProductCard = ({ product, showSizing, onClick }) => {
  const p = product;
  const hasSafety = (p.safe_on && p.safe_on.length > 0) || (p.not_safe_on && p.not_safe_on.length > 0);
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`ask-moe-product-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-foreground leading-tight">{p.name}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
            {p.npk && <><span className="font-medium">{p.npk}</span><span>·</span></>}
            <span className="capitalize">{p.type}</span>
            {p.slow_release_pct > 0 && (
              <>
                <span>·</span>
                <span>{p.slow_release_pct}% slow-release</span>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{p.why}</p>

          {/* State restriction warning — CYA (matches user's property state) */}
          {p.restricted_here?.restricted && (
            <div
              className="mt-2 flex items-start gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-800 dark:text-red-300"
              data-testid={`ask-moe-product-restricted-${p.id}`}
            >
              <Warning weight="fill" size={14} className="text-red-600 dark:text-red-400 shrink-0 mt-[1px]" />
              <span>
                <strong>Restricted in {p.restricted_here.state}</strong> — {p.restricted_here.reason}
              </span>
            </div>
          )}

          {/* Safe-on / not-safe-on badges (post-em products) */}
          {hasSafety && (
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.safe_on || []).map((g) => (
                <span key={`s-${g}`} className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
                  ✓ {g}
                </span>
              ))}
              {(p.not_safe_on || []).map((g) => (
                <span key={`n-${g}`} className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-700 dark:text-red-400 border border-red-500/20">
                  ✗ {g}
                </span>
              ))}
            </div>
          )}

          {/* Rate note (free-form text for products without a standard N-calc) */}
          {p.rate_note && (
            <div className="mt-2 text-[11px] text-foreground/80 bg-muted/40 rounded px-2 py-1">
              <span className="font-semibold">Rate:</span> {p.rate_note}
            </div>
          )}

          {/* Auto-calc sizing (fertilizer-style guides only) */}
          {showSizing && p.calc && (
            <div className="mt-2 text-[11px] text-foreground/80 bg-muted/50 rounded px-2 py-1 inline-block">
              For your lawn: <strong>~{p.calc.total_lbs_needed} lbs</strong>
              {p.bags_needed ? ` · about ${p.bags_needed} bag${p.bags_needed > 1 ? 's' : ''}` : ''}
            </div>
          )}
        </div>
      </div>
      <a
        href={p.affiliate_link}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={onClick}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
        data-testid={`ask-moe-product-link-${p.id}`}
      >
        Check on Amazon <ArrowSquareOut weight="bold" size={12} />
      </a>
    </div>
  );
};

/**
 * Inline trigger button — renders next to a Smart Plan action title.
 * Hides itself when no guide exists for the given action.
 */
export const AskMoeButton = ({ actionKey, lawnSqft, size = 'sm', label = 'Ask Moe' }) => {
  const [available, setAvailable] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API_URL}/api/action-guides`)
      .then((res) => {
        if (cancel) return;
        const all = new Set([...(res.data.available || []), ...(res.data.category_aliases || [])]);
        setAvailable(all);
      })
      .catch(() => setAvailable(new Set()));
    return () => {
      cancel = true;
    };
  }, []);

  if (!available) return null;
  const key = (actionKey || '').toLowerCase().trim();
  if (!available.has(key)) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:text-primary/80 transition-colors"
        data-testid={`ask-moe-btn-${key}`}
      >
        <Question weight="bold" size={12} />
        {label}
      </button>
      <AskMoeDrawer
        open={open}
        onOpenChange={setOpen}
        actionKey={key}
        lawnSqft={lawnSqft}
      />
    </>
  );
};

export default AskMoeDrawer;
